import React, { useState, useEffect } from 'react';
import './PolicyTable.css';

const PolicyTable = ({ policies, onEdit }) => {
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('policyTableSort');
    return saved
      ? JSON.parse(saved)
      : { key: 'effective_date', direction: 'desc' };
  });

  useEffect(() => {
    localStorage.setItem('policyTableSort', JSON.stringify(sortConfig));
  }, [sortConfig]);

  const formatDateOnly = (value) => {
    if (!value) return '-';

    const dateString = String(value).split('T')[0];
    const [year, month, day] = dateString.split('-');

    if (!year || !month || !day) return '-';

    return `${month}/${day}/${year}`;
  };

  const getDateSortValue = (value) => {
    if (!value) return '';

    const dateString = String(value).split('T')[0];
    const [year, month, day] = dateString.split('-');

    if (!year || !month || !day) return '';

    return `${year}-${month}-${day}`;
  };

  const getCoverageTypeDisplay = (policy) => {
    return (
      policy.coverageTypeName ||
      policy.coverage_type_name ||
      policy.coverage_type ||
      '-'
    );
  };

  const getMedicareSupplementPlanType = (policy) => {
    return (
      policy.coverageTypePlanTypeName ||
      policy.coverage_type_plan_type_name ||
      policy.coverage_type_plan_type ||
      policy.planTypeName ||
      policy.plan_type_name ||
      policy.planType ||
      policy.plan_type ||
      policy.medicare_supplement_plan_type ||
      ''
    );
  };

  const getPlanDisplay = (policy) => {
    const coverageType = getCoverageTypeDisplay(policy);

    if (String(coverageType).trim().toLowerCase() === 'medicare supplement') {
      return (
        getMedicareSupplementPlanType(policy) ||
        policy.plan_name ||
        policy.planName ||
        policy.product_name ||
        '-'
      );
    }

    return (
      policy.plan_name ||
      policy.planName ||
      policy.product_name ||
      policy.plan_type ||
      policy.planType ||
      '-'
    );
  };

  const getPolicyNumberDisplay = (policy) => {
    return policy.policy_number || policy.policyNumber || '-';
  };

  const getSortValue = (policy, key) => {
    switch (key) {
      case 'status':
        return policy.status || '';
      case 'coverageTypeName':
        return getCoverageTypeDisplay(policy);
      case 'carrierName':
        return policy.carrierName || policy.carrier_name || '';
      case 'plan_display':
        return getPlanDisplay(policy);
      case 'policy_number':
        return getPolicyNumberDisplay(policy);
      case 'effective_date':
        return getDateSortValue(policy.effective_date);
      case 'application_date':
        return getDateSortValue(policy.application_date);
      case 'renewal_date':
        return getDateSortValue(policy.renewal_date);
      default:
        return policy[key] ?? '';
    }
  };

  const sortedPolicies = [...policies].sort((a, b) => {
    const { key, direction } = sortConfig;

    let valA = getSortValue(a, key);
    let valB = getSortValue(b, key);

    valA = String(valA).toLowerCase();
    valB = String(valB).toLowerCase();

    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <table className="sales-table policy-sales-table">
      <thead>
        <tr>
          <th onClick={() => handleSort('status')}>
            Status{renderSortArrow('status')}
          </th>
          <th onClick={() => handleSort('coverageTypeName')}>
            Coverage Type{renderSortArrow('coverageTypeName')}
          </th>
          <th onClick={() => handleSort('carrierName')}>
            Carrier{renderSortArrow('carrierName')}
          </th>
          <th onClick={() => handleSort('plan_display')}>
            Plan{renderSortArrow('plan_display')}
          </th>
          <th onClick={() => handleSort('policy_number')}>
            Policy #{renderSortArrow('policy_number')}
          </th>
          <th onClick={() => handleSort('effective_date')}>
            Effective Date{renderSortArrow('effective_date')}
          </th>
        </tr>
      </thead>

      <tbody>
        {sortedPolicies.length === 0 ? (
          <tr>
            <td colSpan="6" className="sales-empty-cell">
              No policies found.
            </td>
          </tr>
        ) : (
          sortedPolicies.map((policy) => (
            <tr
              key={`${getCoverageTypeDisplay(policy)}-${policy.id}`}
              onClick={() => onEdit(policy)}
              className="sales-click-row policy-click-row"
            >
              <td>
                <span className="policy-status-text">{policy.status || '-'}</span>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">
                    {getCoverageTypeDisplay(policy)}
                  </div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">
                    {policy.carrierName || policy.carrier_name || '-'}
                  </div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">{getPlanDisplay(policy)}</div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">{getPolicyNumberDisplay(policy)}</div>
                </div>
              </td>

              <td>{formatDateOnly(policy.effective_date)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

export default PolicyTable;